import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { MagnifyingGlassIcon, PlusIcon, ChevronLeftIcon, CameraIcon, SparklesIcon, PencilSquareIcon, TrashIcon, PaperClipIcon, ChevronDownIcon, ArrowsUpDownIcon, CheckIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import './FoodSelectionModal.css';
import { buildFoodServingHint, formatFoodQuantity, roundFoodQuantity } from './foodQuantityPresentation';
import { pickNativePhotoFiles } from './nativePhotoSource';
import { MAX_MEAL_FOOD_BATCH } from './mealFoodBatchOperations';

const UNIT_OPTIONS = ['serving', 'g', 'ml', 'oz', 'lb', 'cup', 'tbsp', 'tsp', 'fl oz'];

const normalizeUnit = (value, fallback = 'serving') => {
    const unit = String(value || '').trim().toLowerCase().replace(/\.$/, '');
    const aliases = {
        serving: 'serving', servings: 'serving',
        g: 'g', gram: 'g', grams: 'g',
        ml: 'ml', milliliter: 'ml', milliliters: 'ml', millilitre: 'ml', millilitres: 'ml',
        oz: 'oz', ounce: 'oz', ounces: 'oz',
        lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
        cup: 'cup', cups: 'cup',
        tbsp: 'tbsp', tablespoon: 'tbsp', tablespoons: 'tbsp',
        tsp: 'tsp', teaspoon: 'tsp', teaspoons: 'tsp',
        'fl oz': 'fl oz', 'fluid ounce': 'fl oz', 'fluid ounces': 'fl oz'
    };
    return aliases[unit] || fallback;
};

const getAutofillDefaultUnit = (foodData) => {
    const servingText = [foodData?.serving_description, foodData?.serving_unit]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    if (/\btbsp\b|\btablespoons?\b/.test(servingText)) return 'tbsp';
    if (/\btsp\b|\tteaspoons?\b/.test(servingText)) return 'tsp';
    if (/\bcups?\b/.test(servingText)) return 'cup';
    return 'serving';
};

const cleanAutofillServingDescription = (description) => {
    if (!description) return description;

    const text = String(description).trim().replace(/\s+/g, ' ');
    const packageFraction = text.match(/(?:about\s+)?(\d+(?:\.\d+)?\s*\/\s*\d+\s*(?:pkg|package|can|container|box|bag|bottle|carton|jar|tub|packet|pouch))\b/i);
    if (packageFraction) return packageFraction[1].replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ');

    const quantityOnly = text.split(/\s+of\s+/i)[0].replace(/[([{].*$/, '').trim();
    return quantityOnly || text;
};

const sortFoodResults = (foods, sort) => {
    const sorted = [...foods];
    const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' });

    sorted.sort((left, right) => {
        if (sort === 'alphabetical') {
            return compareText(left.name, right.name) || compareText(left.brand, right.brand);
        }

        if (sort === 'date_added') {
            const rightDate = new Date(right.created_at || 0).getTime();
            const leftDate = new Date(left.created_at || 0).getTime();
            return rightDate - leftDate || compareText(left.name, right.name);
        }

        return (Number(right.usage_count) || 0) - (Number(left.usage_count) || 0)
            || compareText(left.name, right.name)
            || compareText(left.brand, right.brand);
    });

    return sorted;
};

// Base conversions to g or ml. 
// Note: Volume conversions are water-approximate or standard culinary definitions
const CONVERSION_RATES = {
  g: 1,
  ml: 1, 
  oz: 28.35,      // Mass
  lb: 453.6,      // Mass
  cup: 240,       // Volume (approx 240ml standard cup)
  tbsp: 15,       // Volume
  tsp: 5,         // Volume
  'fl oz': 29.57, // Volume
  serving: 1
};

const UNIT_TYPES = {
    g: 'mass', oz: 'mass', lb: 'mass',
    ml: 'volume', cup: 'volume', tbsp: 'volume', tsp: 'volume', 'fl oz': 'volume',
    serving: 'serving'
};

const HOLD_TO_SELECT_MS = 1000;
const HOLD_MOVE_TOLERANCE_PX = 10;

// The quantity/unit a picked food starts with. Single taps and multi-select
// share this so a batch adds exactly what the quantity dialog would prefill.
// History applies only to rows that came from the "Usual for this meal" fetch,
// judged by the displayed rows' origin rather than the (possibly newer) query.
const resolveFoodDefaultPair = (food, { suggestionContext, initialQuantity, initialUnit, mealId, resultsOrigin }) => {
    // Use recipe quantity when adding from a meal suggestion
    if (suggestionContext && initialQuantity != null) {
        return { quantity: formatFoodQuantity(initialQuantity), unit: normalizeUnit(initialUnit, 'g') };
    }
    if (
        !suggestionContext && mealId && resultsOrigin === 'usual'
        && ['number', 'string'].includes(typeof food.last_used_quantity)
        && Number.isFinite(Number(food.last_used_quantity)) && Number(food.last_used_quantity) > 0
        && UNIT_OPTIONS.includes(food.last_used_unit)
    ) {
        // Scoped occurrence data is a pair, not a new nutrition serving definition.
        return { quantity: formatFoodQuantity(food.last_used_quantity), unit: food.last_used_unit };
    }
    if (food.serving_size) {
        return { quantity: formatFoodQuantity(food.serving_size), unit: normalizeUnit(food.serving_unit || food.serving_quantity_unit, 'g') };
    }
    return { quantity: 100, unit: 'g' };
};

const FoodSelectionModal = ({ 
  close, 
  onSelect, 
  backendService, 
  startWithCamera = false, 
    startWithPhotos = false,
  manageMode = false, 
  initialFood = null, 
  embedded = false, 
  readOnly = false,
  mealId = null,
  resultsOverride = null,
  suggestionContext = null,
  initialQuantity = null,
  initialUnit = null,
    onSkip = null,
    onStop = null,
    onSelectFoods = null,
    initialSelectedFoods = null,
    initialSelectionState = null
}) => {
    // Opt-in: only the meal Foods tab supplies a batch callback.
    const multiSelectEnabled = typeof onSelectFoods === 'function'
        && !manageMode && !suggestionContext && !initialFood && !readOnly;
    const [selectionMode, setSelectionMode] = useState(() => multiSelectEnabled && !!initialSelectedFoods?.length);
    const [selectedFoods, setSelectedFoods] = useState(() => (multiSelectEnabled && initialSelectedFoods?.length ? initialSelectedFoods : []));
    const [selectionNotice, setSelectionNotice] = useState(() => {
        if (!multiSelectEnabled || !initialSelectedFoods?.length) return null;
        return initialSelectionState === 'pending'
            ? 'Still adding these foods. Adding again joins that add — nothing is added twice.'
            : "Your last add wasn't confirmed. Adding again is safe — foods won't be added twice.";
    });
    const [batchError, setBatchError] = useState(null);
    const [submittingBatch, setSubmittingBatch] = useState(false);
    const submittingRef = useRef(false);
    const holdRef = useRef(null);
    const suppressClickRef = useRef(null);
    const mountedRef = useRef(true);
    const resultsListRef = useRef(null);
    const focusFirstResultRef = useRef(false);
    const selectedFoodsRef = useRef(selectedFoods);
    selectedFoodsRef.current = selectedFoods;

    useEffect(() => {
        // "Select multiple" is replaced by the count; keep keyboard focus in the list.
        if (!selectionMode || !focusFirstResultRef.current) return;
        focusFirstResultRef.current = false;
        resultsListRef.current?.querySelector('.fsm-result-item')?.focus();
    }, [selectionMode]);
    const [view, setView] = useState(
        startWithCamera || startWithPhotos ? 'create' : (suggestionContext ? 'suggestion_pick' : (initialFood ? 'quantity' : 'search'))
    );
    const [query, setQuery] = useState(
        suggestionContext ? (suggestionContext.searchTerm || suggestionContext.ingredient.name) : ''
    );
  const [results, setResults] = useState(resultsOverride || []);
  // Which fetch produced the displayed rows: 'usual', 'search' or 'override'.
  const [resultsOrigin, setResultsOrigin] = useState(resultsOverride ? 'override' : null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [appendError, setAppendError] = useState(null);
    const [foodSort, setFoodSort] = useState('usage');
  const [hasSearched, setHasSearched] = useState(!!resultsOverride);
  const [selectedFood, setSelectedFood] = useState(initialFood);
    const [quantityReturnView, setQuantityReturnView] = useState(suggestionContext ? 'suggestion_pick' : 'search');
    const [createReturnView, setCreateReturnView] = useState('search');
  const [inferring, setInferring] = useState(false);
  const [inferenceProgress, setInferenceProgress] = useState(null);
  const [inferenceResult, setInferenceResult] = useState(null); // Full result with is_multi_ingredient
  const [duplicateCheck, setDuplicateCheck] = useState(null); // { existing: Object, new: Object }
  const inferenceAbortControllerRef = useRef(null);
  const inferenceRequestIdRef = useRef(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  // Only the owner may delete a food; editing anyone else's saves a copy.
  const [editingOwnFood, setEditingOwnFood] = useState(false);

  // Pagination State
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const foodRequestIdRef = useRef(0);
  const appendInFlightRef = useRef(false);
  const appendRequestIdRef = useRef(0);

  // AI Refinement State
  const [description, setDescription] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);

    useEffect(() => {
        const urls = selectedFiles.map(file => URL.createObjectURL(file));
        setPreviewUrls(urls);
        return () => urls.forEach(url => URL.revokeObjectURL(url));
    }, [selectedFiles]);

    useEffect(() => () => {
        inferenceRequestIdRef.current += 1;
        inferenceAbortControllerRef.current?.abort();
        inferenceAbortControllerRef.current = null;
    }, []);

    const openNativePhotoSource = async (source) => {
        const files = await pickNativePhotoFiles(source, Number.MAX_SAFE_INTEGER);
        if (files === null) return false;
        if (files.length) setSelectedFiles(prev => [...prev, ...files]);
        return true;
    };

  // Quantity Step State
  const [quantity, setQuantity] = useState(() => formatFoodQuantity(initialQuantity ?? initialFood?.serving_size ?? 100));
    const [unit, setUnit] = useState(normalizeUnit(initialUnit || initialFood?.serving_unit || initialFood?.serving_quantity_unit, 'g'));

  // Create Step State
  const [newFood, setNewFood] = useState({
    name: initialFood?.name || '',
    brand: initialFood?.brand || '',
    calories: initialFood?.calories || '',
    protein: initialFood?.protein || '',
    carbs: initialFood?.carbs || '',
    fat: initialFood?.fat || '',
    water_ml: initialFood?.water_ml || '',
    servingSize: initialFood?.serving_size || 100,
    servingUnit: initialFood?.serving_unit || 'g',
    servingWeight: initialFood?.serving_weight || '',
    servingWeightUnit: 'g',
    servingVolume: initialFood?.serving_volume || '',
    servingVolumeUnit: 'ml',
    servingDescription: initialFood?.serving_description || '',
    density: initialFood?.density || 1
  });

  // Perform Search / Fetch
  const performFetch = useCallback(async (searchTerm, pageNum, isAppend) => {
      const requestId = ++foodRequestIdRef.current;
      if (isAppend) {
          appendRequestIdRef.current = requestId;
          setLoadingMore(true);
          setAppendError(null);
      }
      // Logic for manageMode vs basic search
      const isSearchActive = (searchTerm && searchTerm.length >= 2);
      const isMealSuggestionFetch = !manageMode && !!mealId && !searchTerm && view === 'search';
      const shouldSearch = manageMode || isSearchActive || isMealSuggestionFetch;

      // Reset if not enough chars and not manage mode
      if (!shouldSearch) {
        setResults([]);
        setHasMore(false);
        return;
      }

      if (!isAppend) {
          setLoading(true);
          setFetchError(null);
      }
      
      try {
        const limit = 20;
        const data = isMealSuggestionFetch
            ? await backendService.getMealFoodSuggestions(mealId, pageNum, limit, foodSort)
            : await backendService.getFoods(searchTerm, pageNum, limit, foodSort);
        if (requestId !== foodRequestIdRef.current) return;
        
        if (isAppend) {
            setResults(prev => {
                 // Simple deduplication based on _id
                 const existing = new Set(prev.map(p => p._id));
                 const newItems = data.filter(d => !existing.has(d._id));
                  return sortFoodResults([...prev, ...newItems], foodSort);
            });
        } else {
              setResults(sortFoodResults(data, foodSort));
              setResultsOrigin(isMealSuggestionFetch ? 'usual' : 'search');
        }
        
        if (isAppend) setPage(pageNum);
        setHasMore(data.length === limit);
        setHasSearched(true);
      } catch (err) {
        if (requestId !== foodRequestIdRef.current) return;
        console.error("Search failed", err);
        setHasSearched(true);
        if (isAppend) {
            setAppendError({
                page: pageNum,
                message: 'Could not load more foods.'
            });
        } else {
            setFetchError(isMealSuggestionFetch
                ? 'Could not load your usual foods.'
                : 'Search failed. Please try again.');
        }
      } finally {
        if (isAppend && appendRequestIdRef.current === requestId) {
            appendInFlightRef.current = false;
            setLoadingMore(false);
        }
        if (requestId === foodRequestIdRef.current) setLoading(false);
      }
  }, [backendService, manageMode, mealId, foodSort, view]);

  // Debounced wrapper for Input changes (always page 0)
  const debouncedFetch = useMemo(() => debounce((val) => {
      setPage(0); 
      setHasMore(true);
      performFetch(val, 0, false);
    }, 500), [performFetch]);

    // Auto-trigger the selected photo source when opening create mode.
  useEffect(() => {
        if ((startWithCamera || startWithPhotos) && view === 'create') {
        const timer = setTimeout(() => {
                        const source = startWithPhotos ? 'PHOTOS' : 'CAMERA';
                        openNativePhotoSource(source).then((usedNative) => {
                if (usedNative) return;
                                const input = startWithPhotos ? fileInputRef.current : cameraInputRef.current;
                                input?.click();
            });
        }, 300);
        return () => clearTimeout(timer);
        }
    }, [startWithCamera, startWithPhotos, view]);

  // Query Effect
  useEffect(() => {
    foodRequestIdRef.current += 1;
    appendInFlightRef.current = false;
    appendRequestIdRef.current = 0;
    setLoadingMore(false);
    setFetchError(null);
    setAppendError(null);
    setHasSearched(false);
    // Trigger global management/search, or meal-specific defaults while empty.
    if (manageMode || query.length >= 2 || (mealId && query.length === 0 && view === 'search')) {
        debouncedFetch(query);
        if (!manageMode && mealId && query.length === 0 && view === 'search') debouncedFetch.flush();
    } else {
        setResults([]);
        setLoading(false);
    }
    return () => debouncedFetch.cancel();
    }, [query, debouncedFetch, manageMode, mealId, foodSort, view]);

    const handleFoodSortChange = (event) => {
        setFoodSort(event.target.value);
        setPage(0);
        setHasMore(true);
    };

  const handleScroll = (e) => {
      // Scrolling is not a press-and-hold.
      cancelHold();
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Buffer of 50px
      if (scrollHeight - scrollTop <= clientHeight + 50) {
          if (!loading && !appendInFlightRef.current && !appendError && hasMore) {
              appendInFlightRef.current = true;
              const nextPage = page + 1;
              performFetch(query, nextPage, true);
          }
      }
  };

  const retryFetch = () => {
      setPage(0);
      setHasMore(true);
      performFetch(query, 0, false);
  };

  const retryAppend = () => {
      if (!appendError || appendInFlightRef.current) return;
      appendInFlightRef.current = true;
      performFetch(query, appendError.page, true);
  };

  const handleEditStart = (food) => {
      setCreateReturnView(view);
      setEditingId(food._id);
      const currentUserId = backendService.getUser?.()?._id;
      setEditingOwnFood(Boolean(currentUserId && food.created_by && String(food.created_by) === String(currentUserId)));

      // Auto-populate weight/volume from legacy fields if specific fields are missing
      let weight = food.serving_weight || '';
      let volume = food.serving_volume || '';
      if (!weight && !volume) {
         if (food.serving_unit === 'g') weight = food.serving_size;
         if (food.serving_unit === 'ml') volume = food.serving_size;
      }

      setNewFood({
          name: food.name,
          brand: food.brand || '',
          calories: food.calories,
          protein: food.protein || food.macros?.protein || 0,
          carbs: food.carbs || food.macros?.carbs || 0,
          fat: food.fat || food.macros?.fat || 0,
          water_ml: food.water_ml || food.macros?.water_ml || 0,
          servingSize: food.serving_size || 100,
          servingUnit: food.serving_unit || food.serving_quantity_unit || 'g', 
          servingWeight: weight,
          servingWeightUnit: 'g', // Always g from DB
          servingVolume: volume,
          servingVolumeUnit: 'ml', // Always ml from DB
          servingDescription: food.serving_description || '',
          density: food.density || 1
      });
      setView('create');
  };

  const openCreate = () => {
      setCreateReturnView(view);
      setView('create');
  };

  const handleFoodClick = (food) => {
    if (manageMode) {
        handleEditStart(food);
        return;
    }
    setQuantityReturnView(view);
    setSelectedFood(food);
    const pair = resolveFoodDefaultPair(food, { suggestionContext, initialQuantity, initialUnit, mealId, resultsOrigin });
    setQuantity(pair.quantity);
    setUnit(pair.unit);
    setView('quantity');
  };

  /* --- MULTI-SELECT (meal Foods tab only) --- */

  const toggleFoodSelection = (food) => {
    const selected = selectedFoodsRef.current.some(entry => entry.food._id === food._id);
    if (!selected && selectedFoodsRef.current.length >= MAX_MEAL_FOOD_BATCH) {
        setBatchError(`You can add up to ${MAX_MEAL_FOOD_BATCH} foods at a time.`);
        return;
    }
    setBatchError(null);
    // The default comes from where the food was picked (usual vs typed
    // search) and stays with it through later query/sort/page changes.
    const pair = resolveFoodDefaultPair(food, { suggestionContext, initialQuantity, initialUnit, mealId, resultsOrigin });
    const next = selected
        ? selectedFoodsRef.current.filter(entry => entry.food._id !== food._id)
        : [...selectedFoodsRef.current, { food, quantity: Number(pair.quantity), unit: pair.unit }];
    selectedFoodsRef.current = next;
    setSelectedFoods(next);
  };

  const cancelHold = useCallback(() => {
    if (!holdRef.current) return;
    clearTimeout(holdRef.current.timer);
    holdRef.current = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
        mountedRef.current = false;
        cancelHold();
    };
  }, [cancelHold]);

  const exitSelection = () => {
    cancelHold();
    setSelectionMode(false);
    selectedFoodsRef.current = [];
    setSelectedFoods([]);
    setBatchError(null);
    setSelectionNotice(null);
  };

  // A selection belongs to one meal and one signed-in account. Replacing the
  // scope object also fences late hold timers and submit continuations.
  const selectionScopeRef = useRef({ mealId, backendService });
  const resetSelectionScope = useCallback(() => {
    selectionScopeRef.current = { mealId, backendService };
    cancelHold();
    submittingRef.current = false;
    setSubmittingBatch(false);
    setSelectionMode(false);
    selectedFoodsRef.current = [];
    setSelectedFoods([]);
    setBatchError(null);
    setSelectionNotice(null);
  }, [mealId, backendService, cancelHold]);
  useEffect(() => {
    const scope = selectionScopeRef.current;
    if (scope.mealId === mealId && scope.backendService === backendService) return;
    resetSelectionScope();
  }, [mealId, backendService, resetSelectionScope]);
  useEffect(() => {
    // The service's principal can change in place (sign-out / account switch).
    if (!multiSelectEnabled || typeof backendService?.onAccountScopeChanged !== 'function') return undefined;
    return backendService.onAccountScopeChanged(resetSelectionScope);
  }, [backendService, multiSelectEnabled, resetSelectionScope]);

  const handleItemPointerDown = (event, food) => {
    suppressClickRef.current = null;
    cancelHold();
    if (!multiSelectEnabled || selectionMode || event.button > 0 || event.isPrimary === false) return;
    const startedSelection = selectionScopeRef.current;
    const hold = {
        foodId: food._id,
        pointerType: event.pointerType,
        x: event.clientX,
        y: event.clientY,
        recognize: () => {
            if (holdRef.current !== hold) return;
            cancelHold();
            if (!mountedRef.current || selectionScopeRef.current !== startedSelection) return;
            suppressClickRef.current = food._id;
            setSelectionMode(true);
            toggleFoodSelection(food);
        },
    };
    holdRef.current = hold;
    // Android Chromium/WebView exposes native touch recognition via contextmenu.
    // Do not race its OS-configured delay with an independent app timer. iOS
    // Safari/WKWebView does not expose that event; retain a fallback there and
    // in other unqualified environments, plus the explicit Select multiple UI.
    const nativeTouchHold = event.pointerType === 'touch'
        && /Android/i.test(navigator.userAgent) && /Chrome\//i.test(navigator.userAgent);
    if (!nativeTouchHold) hold.timer = setTimeout(hold.recognize, HOLD_TO_SELECT_MS);
  };

  const handleItemPointerMove = (event) => {
    const hold = holdRef.current;
    if (!hold) return;
    if (Math.hypot(event.clientX - hold.x, event.clientY - hold.y) > HOLD_MOVE_TOLERANCE_PX) cancelHold();
  };

  const handleItemClick = (food) => {
    if (suppressClickRef.current === food._id) {
        // Trailing click from the press that just entered selection mode.
        suppressClickRef.current = null;
        return;
    }
    if (selectionMode) toggleFoodSelection(food);
    else handleFoodClick(food);
  };

  const handleItemKeyDown = (event, food) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (selectionMode) toggleFoodSelection(food);
    else handleFoodClick(food);
  };

  const handleItemContextMenu = (event, food) => {
    // Only the still-active stationary touch owns native recognition. Movement,
    // scroll, release and pointercancel all retire it before this handler.
    const hold = holdRef.current;
    // React wraps contextmenu as a MouseEvent; read its native PointerEvent
    // provenance rather than mistaking a keyboard/right-click menu for touch.
    const pointerType = event.nativeEvent?.pointerType;
    if (hold?.foodId === food._id && hold.pointerType === 'touch'
        && pointerType === 'touch') {
        event.preventDefault();
        hold.recognize();
    } else if (hold?.foodId === food._id || suppressClickRef.current === food._id) {
        event.preventDefault();
    }
  };

  const submitSelection = async () => {
    if (submittingRef.current || !selectedFoods.length) return;
    submittingRef.current = true;
    const scope = selectionScopeRef.current;
    setSubmittingBatch(true);
    setBatchError(null);
    try {
        const outcome = await onSelectFoods(selectedFoods.map(({ food, quantity: itemQuantity, unit: itemUnit }) => ({
            food, quantity: itemQuantity, unit: itemUnit,
        })));
        if (!mountedRef.current || selectionScopeRef.current !== scope) return;
        if (outcome?.status === 'stale') {
            // Not a confirmed add: the meal or account changed underneath this picker.
            setSelectionNotice(null);
            setBatchError(outcome.dispatched
                ? 'This meal changed before the add was confirmed. Reopen the meal to check it.'
                : 'This meal is no longer open here. Nothing was added.');
            return;
        }
        close();
    } catch (error) {
        if (!mountedRef.current || selectionScopeRef.current !== scope) return;
        setSelectionNotice(null);
        setBatchError(['busy', 'rejected', 'previous-committed'].includes(error?.foodBatchOutcome)
            ? error.message
            : "Couldn't confirm these foods were added. Retry is safe — they won't be added twice.");
    } finally {
        if (mountedRef.current && selectionScopeRef.current === scope) {
            submittingRef.current = false;
            setSubmittingBatch(false);
        }
    }
  };

  const getRatio = () => {
    if (!selectedFood) return 1;
    const userQuantity = parseFloat(quantity) || 0;

    // Get the food's serving definitions
    const servingWeight = parseFloat(selectedFood.serving_weight) || null;
    const servingVolume = parseFloat(selectedFood.serving_volume) || null;
    const density = parseFloat(selectedFood.density) || 1;
    
    // Fallback calculation if no serving_weight/volume
    let gramsPerServing = servingWeight;
    let mlPerServing = servingVolume;
    
    if (!servingWeight && !servingVolume) {
      const sSize = parseFloat(selectedFood.serving_size) || 100;
            const sUnit = normalizeUnit(selectedFood.serving_quantity_unit || selectedFood.serving_unit, 'g');
      if (['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(sUnit)) {
        mlPerServing = sSize * (CONVERSION_RATES[sUnit] || 1);
        gramsPerServing = mlPerServing * density;
      } else {
        gramsPerServing = sSize * (CONVERSION_RATES[sUnit] || 1);
        mlPerServing = density > 0 ? gramsPerServing / density : gramsPerServing;
      }
    } else {
      gramsPerServing = servingWeight || (servingVolume * density);
      mlPerServing = servingVolume || (servingWeight / density);
    }
    
    const isVolumeUnit = (u) => ['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(u);
    const isMassUnit = (u) => ['g', 'oz', 'lb'].includes(u);

    // If user unit is 'serving', logic is simple: they want X servings
    if (unit === 'serving') {
        return userQuantity;
    }

    // Calculate servings from user's quantity/unit
    let servings = 0;
    if (isVolumeUnit(unit)) {
      const mlValue = userQuantity * (CONVERSION_RATES[unit] || 1);
      servings = mlPerServing > 0 ? mlValue / mlPerServing : userQuantity;
    } else if (isMassUnit(unit)) {
      const gramsValue = userQuantity * (CONVERSION_RATES[unit] || 1);
      servings = gramsPerServing > 0 ? gramsValue / gramsPerServing : userQuantity;
    } else {
      servings = gramsPerServing > 0 ? userQuantity / gramsPerServing : userQuantity;
    }

    return servings;
  };

  // Helper to calculate servings for display (same logic as getRatio but exposed for rendering)
  const calculateServingsForDisplay = () => {
    if (!selectedFood) return null;
    return getRatio();
  };

  const canAddSelectedFood = Boolean((!initialFood || suggestionContext) && !readOnly);

  const handleAddStart = (event) => {
    event?.preventDefault();
    const parsedQuantity = Number(quantity);
    if (String(quantity).trim() === '' || !Number.isFinite(parsedQuantity) || parsedQuantity < 0) return;
    if (canAddSelectedFood && selectedFood) {
      onSelect(selectedFood._id, parsedQuantity, unit);
      close();
    }
  };

  const handleAddIngredientIndividually = async (ingredient) => {
    onSelect('create_virtual', 1, 'serving', {
      name: ingredient.name,
      calories: parseFloat(ingredient.calories),
      protein: parseFloat(ingredient.protein) || 0,
      carbs: parseFloat(ingredient.carbs) || 0,
      fat: parseFloat(ingredient.fat) || 0,
      water_ml: parseFloat(ingredient.water_ml) || 0,
      serving_size: parseFloat(ingredient.serving_size) || 1,
      serving_unit: ingredient.serving_unit || ingredient.default_unit || 'serving',
      source: 'user'
    });
  };

  const handleCreateFood = async (e, forceCreate = false) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate. Note: calories can legitimately be 0 (water, black coffee,
      // diet drinks), so check for an empty/invalid value rather than falsiness.
      const nameMissing = !newFood.name || !String(newFood.name).trim();
      const caloriesMissing =
        newFood.calories === '' ||
        newFood.calories === null ||
        newFood.calories === undefined ||
        isNaN(parseFloat(newFood.calories));
      if (nameMissing || caloriesMissing) {
        alert("Name and Calories are required");
        setLoading(false);
        return;
      }
      
      // Logic to determine primary serving size/unit and density
      
      // Calculate normalized values for density calculation
      const weightVal = parseFloat(newFood.servingWeight);
      const volumeVal = parseFloat(newFood.servingVolume);
      
      const normalizedWeight = weightVal ? weightVal * (CONVERSION_RATES[newFood.servingWeightUnit] || 1) : null;
      const normalizedVolume = volumeVal ? volumeVal * (CONVERSION_RATES[newFood.servingVolumeUnit] || 1) : null;

      let calculatedDensity = parseFloat(newFood.density) || 1;

      if (normalizedWeight && normalizedVolume) {
          calculatedDensity = normalizedWeight / normalizedVolume;
      }

      // Determine final Default/Reference Serving size
      let finalServingSize = parseFloat(newFood.servingSize);
      let finalServingUnit = newFood.servingUnit;

      // Logic to auto-calculate size if based on preferred unit
      if (finalServingUnit === 'serving') {
          finalServingSize = 1;
      } else if (UNIT_TYPES[finalServingUnit] === 'mass' && normalizedWeight) {
         // Convert normalized grams back to target unit
         finalServingSize = normalizedWeight / (CONVERSION_RATES[finalServingUnit] || 1);
      } else if (UNIT_TYPES[finalServingUnit] === 'volume' && normalizedVolume) {
         // Convert normalized ml back to target unit
         finalServingSize = normalizedVolume / (CONVERSION_RATES[finalServingUnit] || 1);
      } else if (finalServingUnit === 'g' && normalizedWeight) {
         finalServingSize = normalizedWeight;
      } else if (finalServingUnit === 'ml' && normalizedVolume) {
         finalServingSize = normalizedVolume;
      }
      
      // Fallback if size is NaN or 0 (should stay what user might have hardcoded if logic fails, but usually logic prevails)
      if (!finalServingSize || isNaN(finalServingSize)) finalServingSize = 1;

      const persistedServingSize = roundFoodQuantity(finalServingSize);
      const persistedServingWeight = roundFoodQuantity(normalizedWeight);
      const persistedServingVolume = roundFoodQuantity(normalizedVolume);

      const foodToCreate = {
          name: newFood.name,
          brand: newFood.brand,
          calories: parseFloat(newFood.calories),
          protein: parseFloat(newFood.protein) || 0,
          carbs: parseFloat(newFood.carbs) || 0,
          fat: parseFloat(newFood.fat) || 0,
          water_ml: parseFloat(newFood.water_ml) || 0,
          serving_size: persistedServingSize,
          serving_unit: finalServingUnit,
          serving_weight: persistedServingWeight,
          serving_volume: persistedServingVolume,
          serving_description: newFood.servingDescription || '',
          density: calculatedDensity
      };

      // Check for duplicates before creating (unless forced)
      if (!forceCreate && !editingId) {
        const potentialDups = await backendService.getFoods(newFood.name);
        // Strict-ish match: Same name (case insensitive), similar brand (if provided)
        const dup = potentialDups.find(f => 
            f.name.toLowerCase() === newFood.name.toLowerCase() &&
            (!f.brand || !newFood.brand || f.brand.toLowerCase() === newFood.brand.toLowerCase())
        );

        if (dup) {
            setDuplicateCheck({ existing: dup, new: foodToCreate });
            setLoading(false);
            return;
        }
      }

      // If editing an existing local food
      if (editingId && !String(editingId).startsWith('off_')) {
          await backendService.updateFood(editingId, foodToCreate);

          // If we were editing the food we opened the modal with, close on save for better UX
          if (initialFood && editingId === initialFood._id) {
              close();
              return;
          }

          // Refresh search or update list if we have it
          setPage(0);
          performFetch(query, 0, false); // Reload search results to show updated data
          setView('search');
          setEditingId(null);
          // Reset create form
          setNewFood({
            name: '', brand: '', calories: '', protein: '', carbs: '', fat: '', water_ml: '',
            servingSize: 100, servingUnit: 'g', density: 1
          });
      } else {
          // Creating new OR Cloning external (off_) food
          const created = await backendService.createFood(foodToCreate);
          
          if (editingId) {
             // If we were technically "editing" (an external food), clear that state
             setEditingId(null);
          }

          // Automatically select and add
          onSelect(created._id, persistedServingSize, finalServingUnit);
          
          if (manageMode) {
              // Stay in modal, go back to search to see new item
              setPage(0);
              const searchQuery = newFood.name; // Search for the new item
              setQuery(searchQuery); // Update state to reflect what we are seeing
              performFetch(searchQuery, 0, false);
              
              setView('search');
              // Reset create form
              setNewFood({
                name: '', brand: '', calories: '', protein: '', carbs: '', fat: '', water_ml: '',
                servingSize: 100, servingUnit: 'g', density: 1
              });
          } else {
              close();
          }
      }

    } catch (err) {
        console.error("Failed to create/update food", err);
        alert("Action failed: " + err.message);
    } finally {
        setLoading(false);
    }
  };

  const handleResolveDuplicate = async (action) => {
      if (!duplicateCheck) return;
      
      const { existing, new: newFoodData } = duplicateCheck;

      try {
        if (action === 'use_existing') {
            onSelect(existing._id, newFoodData.serving_size, newFoodData.serving_unit);
            close();
        } else if (action === 'update') {
            setLoading(true);
            // Editing a food the user doesn't own saves a copy with a new _id.
            const saved = await backendService.updateFood(existing._id, newFoodData);
            onSelect(saved?._id || existing._id, newFoodData.serving_size, newFoodData.serving_unit);
            close();
        } else if (action === 'create_new') {
            // Call create recursively with force=true
            handleCreateFood({ preventDefault: () => {} }, true);
        }
      } catch (err) {
        console.error("Failed to resolve duplicate", err);
        alert("Action failed");
        setLoading(false);
      }
  };

    const cancelInference = () => {
        inferenceRequestIdRef.current += 1;
        inferenceAbortControllerRef.current?.abort();
        inferenceAbortControllerRef.current = null;
        setInferring(false);
        setInferenceProgress(null);
    };

    const handleInference = async (descToUse) => {
    inferenceAbortControllerRef.current?.abort();
    const controller = new AbortController();
    inferenceAbortControllerRef.current = controller;
    const requestId = ++inferenceRequestIdRef.current;
    setInferring(true);
    setInferenceProgress(selectedFiles.length
        ? { phase: 'uploading', percent: 0 }
        : { phase: 'analyzing', percent: null });
    try {
        // Construct current context from newFood
        const currentContext = { ...newFood };

                const data = await backendService.inferFood(
                    selectedFiles,
                    descToUse,
                    currentContext,
                    (progress) => {
                        if (requestId === inferenceRequestIdRef.current && !controller.signal.aborted) {
                            setInferenceProgress(progress);
                        }
                    },
                    controller.signal
                );
        if (requestId !== inferenceRequestIdRef.current || controller.signal.aborted) return;
        setInferenceResult(data);

        // If it's multi-ingredient, the AI Summary is still the default fallback
        const foodData = data.summary_food || data;

        setNewFood(prev => ({
            ...prev,
            name: foodData.name || prev.name,
            brand: foodData.brand || prev.brand,
            calories: (foodData.calories !== undefined) ? foodData.calories : prev.calories,
            protein: (foodData.protein !== undefined) ? foodData.protein : prev.protein,
            carbs: (foodData.carbs !== undefined) ? foodData.carbs : prev.carbs,
            fat: (foodData.fat !== undefined) ? foodData.fat : prev.fat,
            water_ml: (foodData.water_ml !== undefined) ? foodData.water_ml : prev.water_ml,
            servingSize: foodData.serving_size || prev.servingSize,
            servingUnit: getAutofillDefaultUnit(foodData),
            servingDescription: cleanAutofillServingDescription(foodData.serving_description) || prev.servingDescription,
            servingWeight: (foodData.serving_weight !== undefined) ? foodData.serving_weight : prev.servingWeight,
            servingWeightUnit: foodData.serving_weight_unit || prev.servingWeightUnit,
            servingVolume: (foodData.serving_volume !== undefined) ? foodData.serving_volume : prev.servingVolume,
            servingVolumeUnit: foodData.serving_volume_unit || prev.servingVolumeUnit,
            density: foodData.estimated_density || prev.density || 1
        }));
    } catch (err) {
        if (requestId !== inferenceRequestIdRef.current || controller.signal.aborted || err.code === 'ERR_CANCELED') return;
        console.error("Inference failed", err);
        if (err.message && err.message.includes('FILE_CHANGED')) {
           alert("File upload failed because the file was modified or moved. Please try selecting the image again.");
        } else if (err.code === 'upload_timeout' || err.code === 'model_timeout') {
           alert(err.message || "Food analysis took too long. Please try again; your photos and description are still attached.");
        } else if (err.code === 'rate_limited') {
           alert("Too many requests right now. Please wait a moment and try again.");
        } else if (err.code === 'upstream_error' || err.code === 'parse_error') {
           alert("The AI service had trouble. Please try again in a moment.");
        } else {
           alert(err.message || "Failed to analyze. Please try again.");
        }
    } finally {
        if (requestId !== inferenceRequestIdRef.current) return;
        inferenceAbortControllerRef.current = null;
        setInferring(false);
        setInferenceProgress(null);
        // Delay clearing the input slightly to ensure browser release
        setTimeout(() => {
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (cameraInputRef.current) cameraInputRef.current.value = '';
        }, 100);
    }
  };

  const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setSelectedFiles(prev => [...prev, ...files]);
  };

    const removeSelectedFile = (indexToRemove) => {
        setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    };

  /* --- RENDERERS --- */

  const renderSearch = () => (
    <div className="fsm-step-search">
            {suggestionContext && (
                <div className="fsm-header">
                    <button className="fsm-back-btn" onClick={() => setView('suggestion_pick')} aria-label="Back to suggested foods">
                        <ChevronLeftIcon className="fsm-icon-small" />
                    </button>
                    <div style={{flex: 1}}></div>
                </div>
            )}
            <div className="fsm-search-controls">
                <div className="fsm-search-bar">
                    <MagnifyingGlassIcon className="fsm-icon" />
                      <input
                              type="text"
                              placeholder={manageMode ? "Search foods to edit..." : "Search foods..."}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoFocus
                    />
                </div>

                        <label className="fsm-sort-control" title="Sort food results" aria-label="Sort food results">
                                <ArrowsUpDownIcon className="fsm-sort-label" aria-hidden="true" />
                <span className="fsm-sort-select-wrap">
                    <select value={foodSort} onChange={handleFoodSortChange}>
                        <option value="usage">Most used</option>
                        <option value="alphabetical">Alphabetical</option>
                        <option value="date_added">Date added</option>
                    </select>
                    <ChevronDownIcon className="fsm-sort-icon" />
                </span>
            </label>
      </div>

      {multiSelectEnabled && (
          <div className="fsm-selection-bar">
              {selectionMode ? (
                  <span className="fsm-selection-count" role="status">{selectedFoods.length} selected</span>
              ) : (
                  <>
                      <span className="fsm-selection-hint">Hold a food to select several</span>
                      <button
                          type="button"
                          className="fsm-select-toggle"
                          onClick={() => {
                              focusFirstResultRef.current = true;
                              setSelectionMode(true);
                          }}
                      >
                          <CheckCircleIcon className="fsm-icon-small" aria-hidden="true" /> Select multiple
                      </button>
                  </>
              )}
          </div>
      )}

      <div className="fsm-results-list" onScroll={handleScroll} ref={resultsListRef}>
        {loading && page === 0 && (
            <div className="fsm-loading-container">
                <div className="fsm-spinner"></div>
            </div>
        )}

        {!loading && fetchError && (
            <div className="fsm-empty-state">
                <p>{fetchError}</p>
                <button className="fsm-btn-secondary" onClick={retryFetch}>Retry</button>
            </div>
        )}

        {!loading && !fetchError && mealId && query.length === 0 && results.length > 0 && (
            <div className="fsm-suggestions-heading">Usual for this meal</div>
        )}
        
        {!loading && !fetchError && hasSearched && results.length === 0 && (manageMode || query.length >= 2) && (
            <div className="fsm-empty-state">
                <p>No results found.</p> 
                <button className="fsm-btn-secondary" onClick={openCreate}>
                  <PlusIcon className="fsm-icon-small" /> Create New Food
                </button>
            </div>
        )}

        {!loading && !fetchError && hasSearched && mealId && query.length === 0 && results.length === 0 && (
            <div className="fsm-empty-state">
                <p>No usual foods to add yet. Search to find something else.</p>
            </div>
        )}

        {!loading && !fetchError && results.length === 0 && query.length < 2 && !manageMode
            && (!mealId || query.length > 0) && (
             <div className="fsm-empty-state">
                <p>Type to search</p>
             </div>
        )}

        {results.map(food => {
            const checked = selectionMode && selectedFoods.some(entry => entry.food._id === food._id);
            return (
            <div
                key={food._id}
                className={`fsm-result-item${selectionMode ? ' selectable' : ''}${checked ? ' selected' : ''}`}
                role={selectionMode ? 'checkbox' : 'button'}
                aria-checked={selectionMode ? checked : undefined}
                tabIndex={0}
                onClick={() => handleItemClick(food)}
                onKeyDown={(event) => handleItemKeyDown(event, food)}
                onPointerDown={(event) => handleItemPointerDown(event, food)}
                onPointerMove={handleItemPointerMove}
                onPointerUp={cancelHold}
                onPointerCancel={cancelHold}
                onPointerLeave={cancelHold}
                onContextMenu={(event) => handleItemContextMenu(event, food)}
            >
                {selectionMode && (
                    <span className={`fsm-check${checked ? ' checked' : ''}`} aria-hidden="true">
                        {checked && <CheckIcon className="fsm-check-icon" />}
                    </span>
                )}
                <div className="fsm-item-main">
                    <span className="fsm-item-name">{food.name}</span>
                    <span className="fsm-item-brand">{food.brand || 'Generic'}</span>
                </div>
                <div className="fsm-item-meta">
                    <span className="fsm-usage-count">
                        {food.meal_usage_count != null
                            ? `Used in ${Number(food.meal_usage_count) || 0} meals`
                            : `Used ${Number(food.usage_count) || 0} times`}
                    </span>
                    {manageMode ? (
                        <PencilSquareIcon className="fsm-icon-small" style={{marginLeft: 'auto', color: '#9ca3af'}} />
                    ) : (
                        <>
                            <span className="fsm-tag">{food.source === 'openfoodfacts' ? 'OFF' : 'Local'}</span>
                            <span className="fsm-cals">{food.calories} kcal</span>
                        </>
                    )}
                </div>
            </div>
            );
        })}

        {appendError && !loadingMore && (
            <div className="fsm-pagination-error">
                <span>{appendError.message}</span>
                <button className="fsm-btn-secondary" onClick={retryAppend}>Retry</button>
            </div>
        )}

        {loadingMore && (
            <div style={{textAlign: 'center', padding: '10px', color: '#666'}}>
                Loading more...
            </div>
        )}
      </div>

      {selectionMode ? (
          <div className="fsm-search-footer fsm-selection-footer">
              {selectionNotice && <p className="fsm-selection-notice">{selectionNotice}</p>}
              {batchError && <p className="fsm-selection-error" role="alert">{batchError}</p>}
              <div className="fsm-selection-actions">
                  <button type="button" className="fsm-btn-cancel" onClick={exitSelection} disabled={submittingBatch}>
                      Cancel
                  </button>
                  <button
                      type="button"
                      className="fsm-btn-primary"
                      onClick={submitSelection}
                      disabled={!selectedFoods.length || selectedFoods.length > MAX_MEAL_FOOD_BATCH || submittingBatch}
                  >
                      {submittingBatch
                          ? 'Adding…'
                          : selectedFoods.length
                              ? `Add ${selectedFoods.length} ${selectedFoods.length === 1 ? 'food' : 'foods'}`
                              : 'Add foods'}
                  </button>
              </div>
          </div>
      ) : (
          <div className="fsm-search-footer">
              <button className="fsm-btn-secondary full-width" onClick={openCreate}>
                  <PlusIcon className="fsm-icon-small" /> Create New Food
              </button>
          </div>
      )}
    </div>
  );

    const handleUnitChange = (newUnit) => {
        if (!selectedFood) {
            setUnit(newUnit);
            return;
        }

        // Get the food's serving definitions
        const servingWeight = parseFloat(selectedFood.serving_weight) || null;
        const servingVolume = parseFloat(selectedFood.serving_volume) || null;
        const density = parseFloat(selectedFood.density) || 1;
        
        // Fallback calculation if no serving_weight/volume
        let gramsPerServing = servingWeight;
        let mlPerServing = servingVolume;
        
        if (!servingWeight && !servingVolume) {
            const sSize = parseFloat(selectedFood.serving_size) || 100;
            const sUnit = normalizeUnit(selectedFood.serving_quantity_unit || selectedFood.serving_unit, 'g');
            if (['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(sUnit)) {
                mlPerServing = sSize * (CONVERSION_RATES[sUnit] || 1);
                gramsPerServing = mlPerServing * density;
            } else {
                gramsPerServing = sSize * (CONVERSION_RATES[sUnit] || 1);
                mlPerServing = density > 0 ? gramsPerServing / density : gramsPerServing;
            }
        } else {
            gramsPerServing = servingWeight || (servingVolume * density);
            mlPerServing = servingVolume || (servingWeight / density);
        }

        const currentQty = parseFloat(quantity) || 0;
        const currentUnit = unit;
        
        const isVolumeUnit = (u) => ['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(u);
        const isMassUnit = (u) => ['g', 'oz', 'lb'].includes(u);
        
        if (currentQty > 0 && currentUnit !== newUnit) {
            // Step 1: Convert current quantity to "servings" as intermediate
            let servings = 0;
            
            if (currentUnit === 'serving') {
                servings = currentQty;
            } else if (isVolumeUnit(currentUnit)) {
                const mlValue = currentQty * (CONVERSION_RATES[currentUnit] || 1);
                servings = mlPerServing > 0 ? mlValue / mlPerServing : currentQty;
            } else if (isMassUnit(currentUnit)) {
                const gramsValue = currentQty * (CONVERSION_RATES[currentUnit] || 1);
                servings = gramsPerServing > 0 ? gramsValue / gramsPerServing : currentQty;
            } else {
                servings = gramsPerServing > 0 ? currentQty / gramsPerServing : currentQty;
            }
            
            // Step 2: Convert servings to the new unit
            let newQtyRaw = 0;
            if (newUnit === 'serving') {
                newQtyRaw = servings;
            } else if (isVolumeUnit(newUnit)) {
                const totalMl = servings * mlPerServing;
                newQtyRaw = totalMl / (CONVERSION_RATES[newUnit] || 1);
            } else if (isMassUnit(newUnit)) {
                const totalGrams = servings * gramsPerServing;
                newQtyRaw = totalGrams / (CONVERSION_RATES[newUnit] || 1);
            } else {
                newQtyRaw = servings * gramsPerServing;
            }

            setQuantity(formatFoodQuantity(newQtyRaw));
        }
        setUnit(newUnit);
    };

  const renderQuantity = () => {
    const servingHint = buildFoodServingHint(selectedFood);
    const servingsDisplay = calculateServingsForDisplay();
    
    return (
    <div className="fsm-step-quantity">
        <div className="fsm-header">
            {(!initialFood || suggestionContext) && (
                <button className="fsm-back-btn" onClick={() => setView(quantityReturnView)} aria-label="Back to food selection">
                    <ChevronLeftIcon className="fsm-icon-small" />
                </button>
            )}
            <div style={{flex: 1}}></div>
        </div>
        
        <form className="fsm-quantity-form" onSubmit={handleAddStart}>
            {/* Food Hero Section */}
            <div className="fsm-food-hero">
                <h2 className="fsm-food-name">{selectedFood.name}</h2>
                {selectedFood.brand && (
                    <span className="fsm-food-brand">{selectedFood.brand}</span>
                )}
                {servingHint && (
                    <div className="fsm-food-serving-badge">
                        {servingHint.replace('1 serving = ', '')}
                    </div>
                )}
                {suggestionContext && suggestionContext.ingredient?.quantity && (
                    <div className="fsm-recipe-quantity-hint">
                        Recipe calls for: {suggestionContext.ingredient.quantity}
                    </div>
                )}
            </div>

            {readOnly ? null : (
                <div className="fsm-field-group">
                    <label>Quantity</label>
                    <div className="fsm-input-group">
                        <input 
                            type="number" 
                            value={quantity} 
                            onChange={(e) => setQuantity(e.target.value)}
                            min="0" step="0.01"
                        />
                        <select value={unit} onChange={(e) => handleUnitChange(e.target.value)}>
                            {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>
                    {/* Show servings equivalent when not in serving mode */}
                    {unit !== 'serving' && servingsDisplay !== null && (
                        <div className="fsm-serving-hint">≈ {servingsDisplay.toFixed(2)} servings</div>
                    )}
                </div>
            )}

            <div className="fsm-summary">
                <div className="fsm-stat">
                    <label>Calories</label>
                    <div className="val">{((selectedFood.calories || 0) * getRatio()).toFixed(0)}</div>
                </div>
                <div className="fsm-stat">
                    <label>Protein</label>
                    <div className="val">
                        {( (selectedFood.protein || selectedFood.macros?.protein || 0) * getRatio() ).toFixed(1)}g
                    </div>
                </div>
                 <div className="fsm-stat">
                    <label>Carbs</label>
                    <div className="val">
                        {( (selectedFood.carbs || selectedFood.macros?.carbs || 0) * getRatio() ).toFixed(1)}g
                    </div>
                </div>
                 <div className="fsm-stat">
                    <label>Fat</label>
                    <div className="val">
                        {( (selectedFood.fat || selectedFood.macros?.fat || 0) * getRatio() ).toFixed(1)}g
                    </div>
                </div>
            </div>

            <div style={{display: 'flex', justifyContent: 'center', margin: '12px 0 4px 0'}}>
                <button 
                    type="button" 
                    onClick={() => handleEditStart(selectedFood)} 
                    style={{
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer', 
                        color: '#9ca3af', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        fontSize: '0.9rem',
                        padding: '4px 8px'
                    }}
                >
                    <PencilSquareIcon className="fsm-icon-small" />
                    Edit Food Details
                </button>
            </div>

            {canAddSelectedFood && (
                <div className="fsm-actions">
                    <button type="submit" className="fsm-btn-primary">Add to Meal</button>
                </div>
            )}
        </form>
    </div>
  );
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm("Are you sure you want to delete this food? It will be archived and hidden from future searches.")) return;
    
    setLoading(true);
    try {
        await backendService.deleteFood(editingId);
        setPage(0);
        performFetch(query, 0, false); 
        setView('search');
        setEditingId(null);
        setNewFood({
            name: '', brand: '', calories: '', protein: '', carbs: '', fat: '',
            servingSize: 100, servingUnit: 'g', density: 1
        });
    } catch (err) {
        console.error("Failed to delete food", err);
        alert("Failed to delete food");
    } finally {
        setLoading(false);
    }
  };

  const renderCreate = () => {
    const isMulti = inferenceResult?.is_multi_ingredient && inferenceResult.individual_ingredients?.length > 1;

    return (
    <div className="fsm-step-create">
        <div className="fsm-header">
            <button className="fsm-back-btn" onClick={() => {
                cancelInference();
                setView(createReturnView);
                setEditingId(null);
                setInferenceResult(null);
            }}><ChevronLeftIcon className="fsm-icon-small" /></button>
            <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '10px'}}>
                <h3 style={{margin: 0}}>{editingId ? 'Edit Food' : 'Create New Food'}</h3>
            </div>
        </div>

        <div className="fsm-create-form-content">
            <div className="fsm-ai-section" style={{marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px'}}>
                <div className="fsm-ai-actions">
                    <div className="fsm-camera-section">
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        className="hidden-file-input"
                        accept="image/*" 
                        multiple
                        onChange={handleImageUpload}
                    />
                    <input 
                        type="file" 
                        ref={cameraInputRef}
                        className="hidden-file-input"
                        accept="image/*;capture=camera"
                        capture="environment"
                        onChange={handleImageUpload}
                    />
                    <button
                        className={`fsm-btn-secondary fsm-photo-action ${selectedFiles.length ? 'active-file' : ''}`}
                        onClick={async () => {
                            const usedNative = await openNativePhotoSource('CAMERA');
                            if (usedNative) return;
                            if (!cameraInputRef.current) return;
                            cameraInputRef.current.setAttribute('accept', 'image/*;capture=camera');
                            cameraInputRef.current.setAttribute('capture', 'environment');
                            cameraInputRef.current.click();
                        }} 
                        type="button" 
                        disabled={inferring}
                        aria-label="Take photo"
                        title="Take photo"
                    >
                        <CameraIcon className="fsm-icon-small" />
                    </button>
                    <button 
                        className={`fsm-btn-secondary fsm-photo-action ${selectedFiles.length ? 'active-file' : ''}`}
                        onClick={async () => {
                            const usedNative = await openNativePhotoSource('PHOTOS');
                            if (usedNative) return;
                            fileInputRef.current?.click();
                        }} 
                        type="button" 
                        disabled={inferring}
                        aria-label="Attach photo"
                        title="Attach photo"
                    >
                        <PaperClipIcon className="fsm-icon-small" />
                    </button>
                    </div>

                    <button
                        className="fsm-btn-primary fsm-autofill-action"
                        onClick={() => handleInference(description)}
                        disabled={inferring || (!selectedFiles.length && !description)}
                        type="button"
                        style={{background: 'linear-gradient(90deg, #7c3aed, #4f46e5)', border: 'none'}}
                    >
                        {inferring ? (
                            inferenceProgress?.phase === 'uploading'
                                ? `Uploading ${selectedFiles.length === 1 ? 'photo' : 'photos'}…${Number.isFinite(inferenceProgress.percent) ? ` ${inferenceProgress.percent}%` : ''}`
                                : 'Analyzing photos and details…'
                        ) : (
                            <>
                                <SparklesIcon className="fsm-icon-small" />
                                {selectedFiles.length || description ? 'Auto-fill / Refine' : 'Start Auto-fill'}
                            </>
                        )}
                    </button>
                </div>

                {selectedFiles.length > 0 && (
                    <div className="fsm-photo-previews" aria-label="Selected food photos">
                        {selectedFiles.map((file, index) => (
                            <div className="fsm-photo-preview" key={`${file.name}-${file.lastModified}-${index}`}>
                                <img src={previewUrls[index]} alt={`Selected food ${index + 1}`} />
                                <button
                                    type="button"
                                    className="fsm-photo-remove"
                                    onClick={() => removeSelectedFile(index)}
                                    aria-label={`Remove photo ${index + 1}`}
                                    title="Remove photo"
                                    disabled={inferring}
                                >
                                    <TrashIcon className="fsm-icon-small" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="fsm-desc-caption">
                    Add a description or photos, then use Autofill to estimate the food details.
                </div>
                <textarea
                    className="fsm-desc-input"
                    placeholder="Describe food (e.g. 'Avocado toast') or add details to photo..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    style={{
                        width: '100%', 
                        padding: '8px', 
                        borderRadius: '8px', 
                        background: '#2a2a2a', 
                        border: '1px solid #444', 
                        color: '#fff',
                        resize: 'none',
                        fontSize: '0.9rem'
                    }}
                />

                {inferring && (
                    <div className={`fsm-inference-status ${inferenceProgress?.phase === 'uploading' ? 'uploading' : 'analyzing'}`} role="status" aria-live="polite">
                        {inferenceProgress?.phase === 'uploading' ? (
                            <>
                                <span>Uploading {selectedFiles.length} {selectedFiles.length === 1 ? 'photo' : 'photos'}. Keep this screen open.</span>
                                <div
                                    className="fsm-upload-progress"
                                    role="progressbar"
                                    aria-label="Photo upload progress"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    aria-valuenow={Number.isFinite(inferenceProgress.percent) ? inferenceProgress.percent : undefined}
                                >
                                    <span style={{ width: `${Number.isFinite(inferenceProgress.percent) ? inferenceProgress.percent : 0}%` }} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="fsm-spinner fsm-spinner-small"></div>
                                <span>{selectedFiles.length ? 'Upload complete. Estimating nutrition now.' : 'Estimating nutrition now.'}</span>
                            </>
                        )}
                    </div>
                )}
            </div>

            {isMulti && (
                <div style={{background: '#111827', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #374151'}}>
                    <div style={{color: '#818cf8', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                        🍱 Multiple items detected
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {inferenceResult.individual_ingredients.map((ing, idx) => (
                            <div key={idx} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#1f2937', borderRadius: '8px', border: '1px solid #374151'}}>
                                <div>
                                    <div style={{color: '#fff', fontWeight: 600, fontSize: '0.95rem'}}>{ing.name}</div>
                                    <div style={{color: '#9ca3af', fontSize: '0.85rem'}}>{ing.quantity} · {ing.calories} cal</div>
                                </div>
                                <button
                                    onClick={() => handleAddIngredientIndividually(ing)}
                                    style={{padding: '6px 12px', borderRadius: '6px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', cursor: 'pointer', fontWeight: 600}}
                                >
                                    + Add
                                </button>
                            </div>
                        ))}
                    </div>
                    <div style={{marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #374151', textAlign: 'center'}}>
                        <div style={{color: '#9ca3af', fontSize: '0.8rem', marginBottom: '8px'}}>Or log everything as a single item:</div>
                        <div style={{fontSize: '1rem', color: '#fff', fontWeight: 600}}>{inferenceResult.summary_food.name}</div>
                        <div style={{fontSize: '0.85rem', color: '#9ca3af'}}>{inferenceResult.summary_food.calories} cal total</div>
                    </div>
                </div>
            )}

            <div style={{display: 'flex', alignItems: 'center', margin: '10px 0', color: '#666', fontSize: '0.8rem'}}>
                <div style={{flex: 1, height: '1px', background: '#333'}}></div>
                <span style={{padding: '0 8px'}}>{isMulti ? 'LOG AS ONE ITEM' : (editingId ? 'FOOD DETAILS' : 'MANUAL ENTRY')}</span>
                <div style={{flex: 1, height: '1px', background: '#333'}}></div>
            </div>

            <div className="fsm-form-row">
                <label>Name*</label>
                <input required type="text" value={newFood.name} onChange={e => setNewFood({...newFood, name: e.target.value})} />
            </div>
            <div className="fsm-form-row">
                <label>Brand</label>
                <input type="text" value={newFood.brand} onChange={e => setNewFood({...newFood, brand: e.target.value})} />
            </div>
            <div className="fsm-form-row two-col">
                 <div>
                    <label>Calories (kcal)*</label>
                    <input required type="number" value={newFood.calories} onChange={e => setNewFood({...newFood, calories: e.target.value})} />
                 </div>
            </div>
            <div className="fsm-subtitle">Macros (per serving)</div>
            <div className="fsm-form-row three-col">
                <div>
                    <label>Protein (g)</label>
                    <input type="number" value={newFood.protein} onChange={e => setNewFood({...newFood, protein: e.target.value})} />
                </div>
                 <div>
                    <label>Carbs (g)</label>
                    <input type="number" value={newFood.carbs} onChange={e => setNewFood({...newFood, carbs: e.target.value})} />
                </div>
                 <div>
                    <label>Fat (g)</label>
                    <input type="number" value={newFood.fat} onChange={e => setNewFood({...newFood, fat: e.target.value})} />
                </div>
            </div>
            <div className="fsm-form-row">
                <label>Water (ml per serving)</label>
                <input type="number" value={newFood.water_ml} onChange={e => setNewFood({...newFood, water_ml: e.target.value})} placeholder="0" />
            </div>
            <div className="fsm-subtitle">Serving Information</div>
            <div className="fsm-form-row">
                <label>Description (e.g. "1 large egg", "1 slice")</label>
                <input type="text" value={newFood.servingDescription} onChange={e => setNewFood({...newFood, servingDescription: e.target.value})} placeholder="Optional description" />
            </div>
            <div className="fsm-form-row two-col">
                 <div style={{display: 'flex', gap: '8px', alignItems: 'flex-end'}}>
                    <div style={{flex: 1}}>
                        <label>Mass</label>
                        <input type="number" placeholder="Optional" value={newFood.servingWeight} onChange={e => setNewFood({...newFood, servingWeight: e.target.value})} />
                    </div>
                    <select 
                        value={newFood.servingWeightUnit} 
                        onChange={(e) => setNewFood({...newFood, servingWeightUnit: e.target.value})}
                        style={{width: '60px', marginBottom: '1px'}}
                    >
                        {['g', 'oz', 'lb'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                 </div>
                 <div style={{display: 'flex', gap: '8px', alignItems: 'flex-end'}}>
                    <div style={{flex: 1}}>
                        <label>Volume</label>
                        <input type="number" placeholder="Optional" value={newFood.servingVolume} onChange={e => setNewFood({...newFood, servingVolume: e.target.value})} />
                    </div>
                    <select 
                        value={newFood.servingVolumeUnit} 
                        onChange={(e) => setNewFood({...newFood, servingVolumeUnit: e.target.value})}
                        style={{width: 'auto', minWidth: '60px', marginBottom: '1px'}}
                    >
                        {['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                </div>
            </div>

            <div className="fsm-form-row">
                 <label>Default Unit (used when adding food)</label>
                 <select 
                    value={newFood.servingUnit} 
                    onChange={(e) => setNewFood({...newFood, servingUnit: e.target.value})}
                    style={{width: '100%'}}
                 >
                    {/* Always valid basics */}
                    <option value="g">Grams (g)</option>
                    <option value="ml">Milliliters (ml)</option>
                    
                    {/* Add options based on inputs */}
                    {newFood.servingDescription && <option value="serving">Serving ({newFood.servingDescription})</option>}
                    {newFood.servingWeightUnit && newFood.servingWeightUnit !== 'g' && (
                        <option value={newFood.servingWeightUnit}>{newFood.servingWeightUnit}</option>
                    )}
                    {newFood.servingVolumeUnit && newFood.servingVolumeUnit !== 'ml' && (
                        <option value={newFood.servingVolumeUnit}>{newFood.servingVolumeUnit}</option>
                    )}
                     
                     <option disabled>──────</option>
                     {UNIT_OPTIONS.map(u => (
                         // Don't duplicate options we likely already added above logic
                         (u !== 'g' && u !== 'ml' && u !== 'serving' && u !== newFood.servingWeightUnit && u !== newFood.servingVolumeUnit) ? 
                         <option key={u} value={u}>{u}</option> : null
                     ))}
                 </select>
            </div>
            
            {/* Hidden fallback/legacy fields if needed for internal state logic, but largely superseded by above logic */}
            {/* We still rely on newFood state for these, but UI is now Weight/Volume driven */}

            {editingId && editingOwnFood && (
                <div style={{marginTop: '2rem', borderTop: '1px solid #333', paddingTop: '1rem', paddingBottom: '1rem', marginBottom: '4rem'}}>
                    <button 
                        type="button" 
                        onClick={handleDelete} 
                        className="fsm-btn-secondary"
                        style={{
                            width: '100%', 
                            color: '#ef4444', 
                            borderColor: 'rgba(239, 68, 68, 0.3)', 
                            background: 'rgba(239, 68, 68, 0.05)',
                            justifyContent: 'center'
                        }}
                    >
                        <TrashIcon className="fsm-icon-small" /> Archive / Delete Food
                    </button>
                </div>
            )}
            {/* Added spacer to ensure content is not hidden by the fixed footer */}
            <div style={{ height: '80px' }}></div>
        </div>

        <div className="fsm-actions" style={{position: 'absolute', bottom: 0, left: 0, right: 0, background: '#1e1e1e', padding: '16px', borderTop: '1px solid #333'}}>
            <button type="button" disabled={loading} className="fsm-btn-primary" onClick={handleCreateFood}>
                {loading ? (editingId ? 'Saving...' : 'Creating...') : (editingId ? 'Save Changes' : (initialFood ? 'Save' : (isMulti ? 'Add as Single Item' : 'Create & Add')))}
            </button>
        </div>
    </div>
  );
  };

  const renderDuplicateCheck = () => {
    if (!duplicateCheck) return null;
    const { existing, new: newData } = duplicateCheck;
    
    return (
        <div className="fsm-step-create">
             <div className="fsm-header">
                <button className="fsm-back-btn" onClick={() => setDuplicateCheck(null)}><ChevronLeftIcon className="fsm-icon-small" /></button>
                <h3>Duplicate Found</h3>
             </div>
             
             <div className="fsm-duplicate-check">
                <div className="fsm-dup-warning">
                    We found a food named <strong>"{existing.name}"</strong> in your database. 
                    Would you like to use the existing one, update it with these new values, or create a separate entry?
                </div>
                
                <div className="fsm-comparison">
                     <div className="fsm-comp-card">
                        <div className="fsm-comp-header">Existing</div>
                        <div>{existing.brand || '-'}</div>
                        <div>{existing.calories} kcal</div>
                        <div>P: {existing.protein || existing.macros?.protein || 0}g</div>
                     </div>
                     <span style={{alignSelf:'center', color:'#999'}}>vs</span>
                     <div className="fsm-comp-card new">
                        <div className="fsm-comp-header">New / Photo</div>
                        <div>{newData.brand || '-'}</div>
                        <div>{newData.calories} kcal</div>
                        <div>P: {newData.protein || 0}g</div>
                     </div>
                </div>

                <div className="fsm-dup-actions">
                    <button className="fsm-btn-dup-use" onClick={() => handleResolveDuplicate('use_existing')}>
                        Use Existing (Discard New)
                    </button>
                    <button className="fsm-btn-dup-update" onClick={() => handleResolveDuplicate('update')}>
                        Update Existing (Overwrite)
                    </button>
                    <button className="fsm-btn-dup-create" onClick={() => handleResolveDuplicate('create_new')}>
                        Create New (Keep Both)
                    </button>
                </div>
             </div>
        </div>
    );
  };

  const renderSuggestionPick = () => {
    const isBatch = suggestionContext?.isBatch;
    const progressText = isBatch ? `(${suggestionContext.idx + 1}/${suggestionContext.total}) ` : '';

    const handleSkip = () => {
      if (onSkip) onSkip();
      close();
    };

        const handleStop = () => {
            if (onStop) onStop();
            close();
        };

    return (
      <div className="fsm-step-search">
        <div className="fsm-header">
          <button className="fsm-back-btn" onClick={close}><ChevronLeftIcon className="fsm-icon-small" /></button>
          <h3 style={{margin: 0}}>{isBatch ? 'Add All: ' : 'Select Food'}{progressText}</h3>
        </div>
        
        <div style={{padding: '16px', background: 'rgba(129, 140, 248, 0.1)', borderRadius: '12px', margin: '0 16px 16px 16px', border: '1px solid rgba(129, 140, 248, 0.2)'}}>
          <div style={{fontSize: '0.8rem', color: '#818cf8', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px'}}>AI Suggestion</div>
          <div style={{fontSize: '1.1rem', fontWeight: 700, color: '#fff'}}>{suggestionContext.ingredient.name}</div>
          <div style={{fontSize: '0.85rem', color: '#9ca3af'}}>{suggestionContext.ingredient.quantity} · {suggestionContext.ingredient.protein}g P · {suggestionContext.ingredient.calories} cal</div>
        </div>

        <div className="fsm-results-list" style={{flex: 1}}>
          {results.length > 0 && (
            <div style={{padding: '0 16px 8px 16px', fontSize: '0.8rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase'}}>
              Possible matches in your DB:
            </div>
          )}
          
          {results.map(food => (
            <div key={food._id} className="fsm-result-item" onClick={() => handleFoodClick(food)}>
              <div className="fsm-item-main">
                <span className="fsm-item-name">{food.name}</span>
                <span className="fsm-item-brand">{food.brand || 'Generic'}</span>
              </div>
              <div className="fsm-item-meta">
                                <span className="fsm-usage-count">Used {Number(food.usage_count) || 0} times</span>
                <span className="fsm-tag">{food.source === 'openfoodfacts' ? 'OFF' : 'Local'}</span>
                <span className="fsm-cals">{food.calories} kcal</span>
              </div>
            </div>
          ))}

          <div style={{padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <button className="fsm-btn-primary full-width" onClick={openCreate} style={{background: 'linear-gradient(90deg, #10b981, #059669)', border: 'none'}}>
              ✨ Create as New Food
            </button>
            <button className="fsm-btn-secondary full-width" onClick={() => setView('search')}>
              🔍 Search Other Foods
            </button>
            <button className="fsm-btn-cancel full-width" onClick={handleSkip}>
              {isBatch ? 'Skip this food' : 'Skip Item'}
            </button>
                        {isBatch && (
                            <button className="fsm-btn-cancel full-width" onClick={handleStop}>
                                Stop adding all
                            </button>
                        )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? "food-selection-embedded h-full" : "food-selection-modal"}>
      {duplicateCheck ? renderDuplicateCheck() : (
        <>
            {view === 'suggestion_pick' && renderSuggestionPick()}
            {view === 'search' && renderSearch()}
            {view === 'quantity' && renderQuantity()}
            {view === 'create' && renderCreate()}
        </>
      )}
    </div>
  );
};

export default FoodSelectionModal;
